import {
    Component,
    ViewChild,
    Inject,
    ViewEncapsulation,
    OnDestroy,
    HostListener,
} from "@angular/core";

import { DOCUMENT } from "@angular/common";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { first, timeout } from "rxjs/operators";

import { AppState } from "@app/store/index";

import * as LayoutActions from "@app/store/layout/layout.actions";
import * as AsideItemActions from "@app/store/aside-item/aside-item.actions";
import * as AsideActions from "@app/store/aside/aside.actions";
import * as ViewerActions from "@app/store/viewer/viewer.actions";
import * as AuthActions from "@app/store/auth/auth.actions";

import { BooksService } from "@app/shared/services/books.service";
import { ResourcesService } from "@app/shared/services/resources.service";
import { EventsService } from "@app/shared/services/events.service";

import { User } from "@app/shared/models";

import { PdfViewerComponent } from "../pdf-viewer/pdf-viewer.component";
import { readAnnotationData } from '../pdf-viewer/models/annotation-data';
import { PdfAnnotation, PdfAnnotationLayerOption, PdfLayerVisibilityOption } from '../pdf-viewer/models/pdf-annotation.model';
import { ViewerPersistenceService } from '../../services/viewer-persistence.service';

declare var noScroll: any;
// Documento vacío que recibe el visor cuando la capa activa es de solo lectura. Nunca se guarda.
const READ_ONLY_LAYER = '{"version":1,"annotations":[]}';


@Component({
    selector: "app-modal-viewer",
    templateUrl: "./modal-viewer.component.html",
    encapsulation: ViewEncapsulation.None,
})
export class ModalViewerComponent implements OnDestroy {

    /*
     * =========================================================
     * ESTADO GENERAL DEL VISOR
     * =========================================================
     */

    openModalViewer: boolean;

    openOptionsMobileViewer: boolean = false;

    isGroupMode: boolean;

    openViewerPanelResources: boolean;

    panelResourcesTypeOpen: string;

    modalContent: any;

    hasBtnOnFooter: boolean;

    /*
     * Indica que existen cambios en las anotaciones
     * que todavía no se han persistido en base de datos.
     */
    private annotationsDirty = false;
    get hasUserChangesNotSave(): boolean { return this.annotationsDirty || this.viewerPersistence.hasPending; }
    annotationsLoadError: string | null = null;
    documentLoadError: string | null = null;
    annotationContext = '';
    private contextRevision = 0;
    private transitionPending = false;
    private transitionSubscription: Subscription | null = null;
    private requestedGroupMode: boolean;
    private unregisterEditor: () => void;
    annotationLayer: 'personal' | 'historical' | 'shared' = 'personal';
    sharedData: any = null;
    sharedCanEdit = false;
    sharedLoadError: string | null = null;
    sharedLoading = false;
    private sharedLoadRevision = 0;
    private sharedLoadSubscription: Subscription | null = null;
    private layerOptionsKey = '';
    private layerOptions: PdfAnnotationLayerOption[] = [];
    // Capas dibujadas sobre el PDF. Es independiente de la capa activa y no se guarda en ningún sitio.
    layerVisibility: { [layer: string]: boolean } = { personal: true, shared: true, historical: true };
    private visibilityOptionsKey = '';
    private visibilityOptions: PdfLayerVisibilityOption[] = [];
    // Resultado de la última copia a Compartidas, para informar en el panel.
    private shareFeedback: { id: string; message?: string } | null = null;

    hideActions: boolean;

    title: string;

    mode: string;

    /*
     * PDF recibido desde el backend.
     *
     * documentPdf se envía al PdfViewerComponent
     * mediante:
     *
     * [pdfSrc]="documentPdf"
     */
    fileData: any;

    public documentPdf: string = null;
    private pdfClosed = false;


    /*
     * =========================================================
     * LIBRO Y CAPÍTULO ACTUAL
     * =========================================================
     */

    public currentBookId = null;

    public currentBook = null;

    public currentChapter = null;

    public currentIndexChapter = 1;

    public currentPage: number = 1;


    /*
     * =========================================================
     * RECURSOS DEL CAPÍTULO
     * =========================================================
     */

    chapterResources: any = [];

    totalResources: number = 0;

    totalVideos: number = 0;

    totalDocuments: number = 0;

    totalImages: number = 0;

    totalLinks: number = 0;


    /*
     * =========================================================
     * USUARIO
     * =========================================================
     */

    currentUser: User;


    /*
     * =========================================================
     * PANTALLA COMPLETA
     * =========================================================
     */

    isInFullScreenMode = false;

    elem: any;


    /*
     * =========================================================
     * SUSCRIPCIONES
     * =========================================================
     */

    private suscriptionsToManage$: Subscription[] = [];


    /*
     * =========================================================
     * GUARDADO AUTOMÁTICO
     * =========================================================
     */

    /*
     * Temporizador utilizado para agrupar cambios rápidos
     * en las anotaciones.
     *
     * Por ejemplo, mientras el usuario escribe una nota
     * no realizamos una petición al backend por cada tecla.
     */
    private annotationsSaveTimeout: any = null;


    /*
     * Tiempo de espera desde el último cambio
     * antes de guardar automáticamente.
     */
    private readonly annotationsAutoSaveDelay = 1000;


    constructor(
        private store: Store<AppState>,
        private booksService: BooksService,
        private resourcesService: ResourcesService,
        private eventsService: EventsService,
        @Inject(DOCUMENT) private document: any,
        public viewerPersistence: ViewerPersistenceService,
    ) {
        this.unregisterEditor = this.viewerPersistence.registerEditor(() => this.prepareAnnotationsSave(),
            () => this.annotationsDirty || !!(this.pdfViewer && this.pdfViewer.hasPendingAnnotationEdit));
        this.initSuscriptions();
    }


    /*
     * =========================================================
     * CICLO DE VIDA
     * =========================================================
     */

    ngOnDestroy() {
        this.sharedLoadRevision++;
        if (this.sharedLoadSubscription) { this.sharedLoadSubscription.unsubscribe(); }
        if (this.transitionSubscription) { this.transitionSubscription.unsubscribe(); }
        this.releaseClosedPdf();
        this.unregisterEditor();


        /*
         * Cancelamos cualquier temporizador que
         * todavía pudiera quedar activo.
         */
        if (this.annotationsSaveTimeout) {

            clearTimeout(
                this.annotationsSaveTimeout,
            );

            this.annotationsSaveTimeout = null;
        }


        /*
         * Cerramos todas las suscripciones creadas
         * durante la vida del componente.
         */
        this.suscriptionsToManage$.forEach(
            (subscription) =>
                subscription.unsubscribe(),
        );
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent) {
        this.prepareAnnotationsSave();
        if (this.hasUserChangesNotSave) {
            event.preventDefault();
            event.returnValue = '';
        }
    }


    /*
     * =========================================================
     * SUSCRIPCIONES AL STORE Y EVENTOS
     * =========================================================
     */

    initSuscriptions() {

        /*
         * Modo individual / grupo.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.isGroup,
                )
                .subscribe((res) => {
                    this.requestedGroupMode = res;
                    if (this.currentChapter && !this.pdfClosed && this.isGroupMode !== res) {
                        const previous = this.isGroupMode;
                        this.withSavedAnnotations(() => {
                            this.isGroupMode = this.requestedGroupMode;
                            this.updateChapter(this.currentChapter);
                        }, () => this.store.dispatch(new ViewerActions.SetIsGroup(previous)));
                    } else { this.isGroupMode = res; }
                }),
        );


        /*
         * Usuario actual.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.auth.user,
                )
                .subscribe((res) => {
                    this.currentUser = res;
                }),
        );


        /*
         * Libro abierto actualmente en el visor.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.book,
                )
                .subscribe((res) => {

                    this.modalContent = res;

                    if (this.modalContent) {
                        this.init();
                    }
                }),
        );


        /*
         * Estado de apertura del modal visor.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.layout.openModalViewer,
                )
                .subscribe((res) => {
                    const wasOpen = this.openModalViewer;
                    this.openModalViewer = res;
                    if (wasOpen && !res) { this.releaseClosedPdf(); }
                    if (!wasOpen && res && this.pdfClosed && this.modalContent) { this.init(); }
                }),
        );


        /*
         * Panel lateral de recursos.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.layout.openViewerPanelResources,
                )
                .subscribe((res) => {
                    this.openViewerPanelResources = res;
                }),
        );


        /*
         * Tipo de panel lateral abierto.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.panelView,
                )
                .subscribe((res) => {
                    this.panelResourcesTypeOpen = res;
                }),
        );


        /*
         * Totales de recursos del capítulo.
         */
        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.totalResources,
                )
                .subscribe((res) => {
                    this.totalResources = res;
                }),
        );

        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.totalVideos,
                )
                .subscribe((res) => {
                    this.totalVideos = res;
                }),
        );

        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.totalImages,
                )
                .subscribe((res) => {
                    this.totalImages = res;
                }),
        );

        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.totalDocuments,
                )
                .subscribe((res) => {
                    this.totalDocuments = res;
                }),
        );

        this.suscriptionsToManage$.push(
            this.store
                .select(
                    (state) =>
                        state.viewer.totalLinks,
                )
                .subscribe((res) => {
                    this.totalLinks = res;
                }),
        );


        /*
         * Cambio de capítulo provocado desde otras
         * partes de la aplicación.
         */
        this.suscriptionsToManage$.push(this.eventsService
            .on(
                this.eventsService
                    .VIEWER_CHANGE_CHAPTER,
            )
            .subscribe((chapter) => {
                this.updateChapter(chapter);
            }));
    }


    /*
     * =========================================================
     * INICIALIZACIÓN DEL VISOR
     * =========================================================
     */

    init() {
        this.withSavedAnnotations(() => this.initBook());
    }

    private initBook() {
        this.pdfClosed = false;

        this.title =
            this.modalContent.name;

        this.mode =
            this.modalContent.typeName;

        this.currentBookId =
            this.modalContent.id;

        this.currentBook =
            this.modalContent;


        /*
         * Guardamos el libro actual en el store.
         */
        const payload = {
            book: this.currentBook,
        };

        this.store.dispatch(
            new ViewerActions.SetBook(
                payload,
            ),
        );


        /*
         * Restauramos el último capítulo; sin marcador válido se abre el primero.
         */
        this.updateChapter(this.getLastChapter());


        /*
         * Elemento utilizado para activar
         * pantalla completa.
         */
        this.elem =
            this.document.documentElement;
    }


    /*
     * =========================================================
     * CAMBIO DE CAPÍTULO
     * =========================================================
     */

    @ViewChild(PdfViewerComponent, { static: false }) pdfViewer: PdfViewerComponent;
    chapterEntry: "first" | "last" | number | null = null;

    navigateToGlobalPage(page: number) {
        const requested = Number(page);
        if (!Number.isInteger(requested) || requested < 1 || requested > Number(this.currentBook.pages)) { return; }
        const chapters = this.currentBook.book_model.bookindex;
        const matches = chapters.filter(chapter => {
            const start = Number(chapter.page_start);
            const end = Number(chapter.page_end);
            return Number.isInteger(start) && start > 0 && Number.isInteger(end) &&
                start <= requested && requested <= end;
        });
        if (matches.length !== 1) { return; }
        const chapter = matches[0];
        this.updateChapter(chapter, requested - Number(chapter.page_start) + 1);
    }

    navigateAdjacentChapter(direction: number) {
        if (!this.documentPdf || (direction !== -1 && direction !== 1)) { return; }
        const chapters = this.currentBook.book_model.bookindex;
        const chapter = chapters[this.currentIndexChapter + direction];
        if (chapter) { this.updateChapter(chapter, direction === 1 ? "first" : "last"); }
    }

    updateChapter(chapter, entry: "first" | "last" | number | null = null) {
        this.withSavedAnnotations(() => this.openChapter(chapter, entry));
    }

    private openChapter(chapter, entry: "first" | "last" | number | null = null) {
        if (this.pdfViewer) { this.pdfViewer.finishAnnotationEdit(); }


        /*
         * Antes de abandonar el capítulo actual,
         * guardamos inmediatamente cualquier cambio
         * de anotaciones que todavía esté pendiente.
         *
         * Es importante hacerlo antes de modificar
         * currentChapter.
         */
        this.flushPendingAnnotationsSave();
        this.documentPdf = null;
        if (typeof this.requestedGroupMode === 'boolean') { this.isGroupMode = this.requestedGroupMode; }
        this.chapterEntry = entry;


        /*
         * Si no se especifica capítulo,
         * abrimos el primero del libro.
         */
        if (!chapter) {

            chapter =
                this.modalContent
                    .book_model
                    .bookindex[0];
        }


        this.currentChapter =
            chapter;


        /*
         * Recuperamos primero las preferencias y
         * anotaciones del usuario para este capítulo.
         *
         * Entre ellas se encuentra la última página
         * visitada, tanto en modo individual como grupo.
         */
        this.updateUserChapterConfig();
        this.annotationContext = `${this.currentBook.id}:${chapter.id}:${this.isGroupMode ? 1 : 0}:${++this.contextRevision}`;
        this.shareFeedback = null;
        if (this.isGroupMode && this.currentBook.currentGroup) {
            this.annotationLayer = 'personal';
            this.loadSharedAnnotations();
        } else {
            this.sharedLoadRevision++;
            this.sharedData = null;
        }
        this.annotationsLoadError = this.activeLayerError();
        this.documentLoadError = null;


        /*
         * Restauramos la última página personal del capítulo, común a Mis libros y al grupo.
         * Sin progreso personal se usa, solo como lectura, la página histórica del grupo.
         */
        const pageConfig = this.currentChapter.userConfig;
        this.currentPage = this.storedPage(pageConfig.hasPersonalProgress ? pageConfig.currentPage : pageConfig.currentPageGroup);


        if (entry) { this.currentPage = 1; }

        /*
         * Posición del capítulo dentro
         * del índice del libro.
         */
        this.currentIndexChapter =
            this.getIndexChapter(
                this.currentChapter,
            );


        /*
         * Cargamos los recursos asociados
         * al capítulo.
         */
        this.initResourcesByChapter();


        /*
         * Actualizamos el store.
         */
        const payload = {

            chapter:
                this.currentChapter,

            resources:
                this.chapterResources,
        };

        this.store.dispatch(
            new ViewerActions.SetChapter(
                payload,
            ),
        );


        /*
         * La página recuperada del capítulo
         * pasa a ser la página global actual.
         */
        this.store.dispatch(
            new ViewerActions.SetGlobalPage(
                this.getGlobalPage(
                    this.currentPage,
                ),
            ),
        );


        /*
         * Finalmente cargamos el PDF del capítulo.
         *
         * PdfViewerComponent recibirá además
         * [initialPage]="currentPage".
         */
        this.getFileChapter();
    }


    getIndexChapter(chapter) {

        let index = 0;

        let indexChapter = -1;


        this.currentBook
            .book_model
            .bookindex
            .forEach((element) => {

                if (
                    element.id ==
                    chapter.id
                ) {

                    indexChapter = index;
                }

                index++;
            });


        return indexChapter;
    }


    previousChapter() {

        this.updateChapter(
            this.currentBook
                .book_model
                .bookindex[
                    this.currentIndexChapter - 1
                ],
        );
    }


    nextChapter() {

        this.updateChapter(
            this.currentBook
                .book_model
                .bookindex[
                    this.currentIndexChapter + 1
                ],
        );
    }


    maxChapters() {

        return this.currentBook
            .book_model
            .bookindex
            .length;
    }


    /*
     * Convierte una página relativa al capítulo
     * en la página global del libro.
     */
    getGlobalPage(page) {

        const chapterStart =
            Number(this.currentChapter.page_start);

        return (
            chapterStart +
            (page - 1)
        );
    }


    /*
     * =========================================================
     * PDF DEL CAPÍTULO
     * =========================================================
     */

    loadedPagination: { pageStart: number; pageEnd: number; bookPages: number } | null = null;
    private chapterLoadRevision = 0;

    // Último tema común: se escribe en la clave individual existente; la de grupo solo se lee como alternativa.
    private lastChapterStorageKeys(): string[] {
        if (!this.currentUser || this.currentUser.id == null || this.currentBookId == null) { return []; }
        const base = `edicioneseo:pdf-viewer:last-chapter:v1:user:${this.currentUser.id}:book:${this.currentBookId}:mode:`;
        return [base + 'individual', base + 'group'];
    }

    private getLastChapter() {
        for (const key of this.lastChapterStorageKeys()) {
            try {
                const id = localStorage.getItem(key);
                if (id === null) { continue; }
                const chapter = this.currentBook.book_model.bookindex.find(item => String(item.id) === id);
                if (chapter) { return chapter; }
                localStorage.removeItem(key);
            } catch (_) { /* El almacenamiento es opcional; conservamos el capítulo inicial. */ }
        }
        return null;
    }

    getFileChapter() {
        const revision = ++this.chapterLoadRevision;
        const storageKey = this.lastChapterStorageKeys()[0];
        const chapterId = this.currentChapter.id;
        const pagination = {
            pageStart: Number(this.currentChapter.page_start),
            pageEnd: Number(this.currentChapter.page_end),
            bookPages: Number(this.currentBook.pages),
        };


        this.booksService
            .openChapter(
                this.currentBookId,
                this.currentChapter.id,
            )
            .pipe(first())
            .subscribe((data) => {
                if (revision !== this.chapterLoadRevision) { return; }
                if (!data || data.error || typeof data.file !== 'string' || !data.file.trim()) {
                    this.documentLoadError = 'No se ha podido cargar este capítulo. Sus anotaciones no se han modificado.';
                    return;
                }
                if (storageKey && chapterId != null && data && !data.error &&
                    typeof data.file === 'string' && data.file.trim()) {
                    try {
                        localStorage.setItem(storageKey, String(chapterId));
                    } catch (_) { /* Un fallo de almacenamiento no debe impedir abrir el PDF. */ }
                }
                this.loadedPagination = pagination;

                /*
                 * Guardamos el PDF recibido.
                 */
                this.fileData =
                    data.file;


                /*
                 * Al modificar documentPdf Angular
                 * envía automáticamente el nuevo PDF
                 * al PdfViewerComponent mediante:
                 *
                 * [pdfSrc]="documentPdf"
                 */
                this.documentPdf =
                    data.file;
            }, () => {
                if (revision !== this.chapterLoadRevision) { return; }
                this.documentLoadError = 'No se ha podido cargar este capítulo. Sus anotaciones no se han modificado.';
            });
    }


    /*
     * =========================================================
     * CONFIGURACIÓN DEL USUARIO POR CAPÍTULO
     * =========================================================
     */

    updateUserChapterConfig() {

        this.currentChapter.userConfig =
            this.findUserChapterConfig();
    }


    findUserChapterConfig() {

        /*
         * Si todavía no existen configuraciones
         * de capítulos para el usuario,
         * devolvemos una configuración vacía.
         */
        if (
            !Array.isArray(this.currentBook.chaptersUser)
        ) {

            return this.loadUserConfig(
                null,
            );
        }


        let userConfig = null;


        this.currentBook
            .chaptersUser
            .forEach((element) => {

                if (
                    element.id ==
                    this.currentChapter.id
                ) {

                    userConfig = element;

                    return;
                }
            });


        return this.loadUserConfig(
            userConfig,
        );
    }


    loadUserConfig(config) {

        /*
         * Configuración inicial de un capítulo
         * que todavía no tiene datos del usuario.
         */
        if (!config) {

            return {

                currentPage: 1,

                currentPageGroup: 1,

                customData: { unavailable: true },

                customDataGroup: { unavailable: true },

                hasPersonalProgress: false,
            };
        }


        return {

            currentPage:
                config.currentPage,

            currentPageGroup:
                config.currentPageGroup,

            customData:
                config.customData === undefined ? { unavailable: true } : config.customData,

            customDataGroup:
                config.customDataGroup === undefined ? { unavailable: true } : config.customDataGroup,

            // Contrato aditivo de Laravel; un backend sin el campo conserva el comportamiento anterior.
            hasPersonalProgress: config.hasPersonalProgress !== false,
        };
    }


    /*
     * =========================================================
     * ANOTACIONES DEL NUEVO VISOR
     * =========================================================
     */


    /*
     * JSON que corresponde al capítulo actual.
     *
     * El PdfViewerComponent lo recibe mediante:
     *
     * [annotationsJson]="currentAnnotationsJson"
     */
    get currentAnnotationsJson(): any {

        if (
            !this.currentChapter ||
            !this.currentChapter.userConfig
        ) {

            return null;
        }


        if (this.isGroupMode && this.currentBook && this.currentBook.currentGroup) {
            // En solo lectura no hay nada editable: las compartidas se dibujan como capa pasiva.
            if (this.annotationReadOnly) { return READ_ONLY_LAYER; }
            if (this.annotationLayer === 'shared') { return this.sharedData; }
            if (this.annotationLayer === 'personal') { return this.currentChapter.userConfig.customData; }
        }
        return this.isGroupMode
            ? this.currentChapter
                  .userConfig
                  .customDataGroup
            : this.currentChapter
                  .userConfig
                  .customData;
    }


    /*
     * Recibe del PdfViewerComponent el JSON actualizado
     * cada vez que se crea, modifica, mueve,
     * recolorea o elimina una anotación.
     */
    onAnnotationsChange(
        json: string,
        context: string = this.annotationContext,
    ): void {

        if (context !== this.annotationContext || this.annotationReadOnly || readAnnotationData(this.currentAnnotationsJson).error ||
            readAnnotationData(json).error) { return; }

        if (
            !this.currentChapter ||
            !this.currentChapter.userConfig
        ) {

            return;
        }


        /*
         * Guardamos inmediatamente el JSON en memoria.
         */
        if (this.isGroupMode && this.currentBook.currentGroup && this.annotationLayer === 'shared') {
            if (!this.sharedCanEdit || !this.sharedData || this.sharedLoadError) { return; }
            this.sharedData = JSON.parse(json);
            this.annotationsDirty = true;
            this.scheduleAnnotationsSave();
            return;
        }
        if (this.isGroupMode && this.currentBook.currentGroup && this.annotationLayer === 'personal') {
            this.currentChapter.userConfig.customData = json;
            const config = (this.currentBook.chaptersUser || []).find(item => item.id == this.currentChapter.id);
            if (config) { config.customData = json; }
            this.annotationsDirty = true;
            this.scheduleAnnotationsSave();
            return;
        }
        if (this.isGroupMode) {

            this.currentChapter
                .userConfig
                .customDataGroup =
                json;

        } else {

            this.currentChapter
                .userConfig
                .customData =
                json;
        }


        /*
         * Actualizamos también currentBook.chaptersUser.
         *
         * Esto permite cambiar de capítulo y volver
         * sin perder las anotaciones aunque todavía
         * no se haya consultado de nuevo el backend.
         */
        this.updateChapterAnnotationsInMemory(
            json,
        );


        /*
         * Existen cambios pendientes
         * de persistir en base de datos.
         */
        this.annotationsDirty = true;


        /*
         * Programamos el guardado automático.
         */
        this.scheduleAnnotationsSave();
    }


    /*
     * Mantiene sincronizada la configuración
     * del capítulo dentro de currentBook.chaptersUser.
     */
    private updateChapterAnnotationsInMemory(
        json: string,
    ): void {

        /*
         * Si no existe todavía la colección,
         * la inicializamos.
         */
        if (
            !this.currentBook.chaptersUser
        ) {

            this.currentBook
                .chaptersUser = [];
        }


        let chapterConfig =
            this.currentBook
                .chaptersUser
                .find(
                    (config) =>
                        config.id ==
                        this.currentChapter.id,
                );


        /*
         * Si el capítulo todavía no tenía
         * configuración de usuario,
         * la creamos también en memoria.
         */
        if (!chapterConfig) {

            chapterConfig = {

                id:
                    this.currentChapter.id,

                currentPage: 1,

                currentPageGroup: 1,

                customData: {},

                customDataGroup: {},
            };


            this.currentBook
                .chaptersUser
                .push(
                    chapterConfig,
                );
        }


        /*
         * Actualizamos las anotaciones
         * individuales o de grupo.
         */
        if (this.isGroupMode) {

            chapterConfig
                .customDataGroup =
                json;

        } else {

            chapterConfig
                .customData =
                json;
        }
    }


    /*
     * =========================================================
     * GUARDADO AUTOMÁTICO DE ANOTACIONES
     * =========================================================
     */


    /*
     * Programa el guardado automático.
     *
     * Si se producen varios cambios seguidos,
     * reiniciamos el temporizador.
     *
     * El backend solo recibe la petición cuando
     * el usuario lleva un segundo sin modificar.
     */
    private scheduleAnnotationsSave(): void {

        if (
            this.annotationsSaveTimeout
        ) {

            clearTimeout(
                this.annotationsSaveTimeout,
            );
        }


        this.annotationsSaveTimeout =
            setTimeout(
                () => {

                    this.annotationsSaveTimeout =
                        null;


                    this.saveViewerConfig();

                },
                this.annotationsAutoSaveDelay,
            );
    }


    /*
     * Fuerza inmediatamente el guardado
     * de cualquier anotación pendiente.
     *
     * Se utiliza antes de:
     *
     * - cambiar de capítulo;
     * - cerrar el visor;
     * - destruir el componente.
     */
    private flushPendingAnnotationsSave(): void {

        if (
            !this.annotationsDirty
        ) {

            return;
        }


        /*
         * Cancelamos el guardado programado,
         * porque vamos a guardar ahora mismo.
         */
        if (
            this.annotationsSaveTimeout
        ) {

            clearTimeout(
                this.annotationsSaveTimeout,
            );

            this.annotationsSaveTimeout =
                null;
        }


        this.saveViewerConfig();
    }


    /*
     * =========================================================
     * CAMBIO DE PÁGINA
     * =========================================================
     */


    /*
     * Recibe el número de página emitido
     * por nuestro PdfViewerComponent.
     *
     */
    pageChange(
        page: number,
    ): void {

        // Mientras se carga otro tema, una emisión del PDF anterior no puede atribuirse al nuevo.
        if (!this.documentPdf || this.pdfClosed) { return; }
        const previousPage = this.currentPage;
        this.currentPage =
            page;


        /*
         * El progreso de lectura es personal y común a Mis libros y al grupo
         * (cualquier capa); la página histórica del grupo no se modifica.
         */
        if (
            this.currentChapter &&
            this.currentChapter.userConfig
        ) {

            this.currentChapter
                .userConfig
                .currentPage =
                page;
            this.currentChapter.userConfig.hasPersonalProgress = true;


            /*
             * Actualizamos también la copia
             * existente en chaptersUser.
             */
            this.updateChapterPageInMemory(
                page,
            );
        }


        /*
         * Actualizamos la página global
         * del libro en el store.
         */
        this.store.dispatch(
            new ViewerActions.SetGlobalPage(
                this.getGlobalPage(
                    page,
                ),
            ),
        );


        /*
         * Las ediciones pendientes de la capa activa se guardan ya, como antes;
         * sin cambios de anotaciones no se reenvía ningún JSON.
         */
        this.flushPendingAnnotationsSave();


        /*
         * Guardado exclusivo del progreso personal, independiente de la cola de anotaciones.
         */
        if (page !== previousPage && this.currentBook && this.currentChapter) {
            this.viewerPersistence.saveProgress(Number(this.currentBook.id), Number(this.currentChapter.id), page);
        }
    }


    /*
     * Mantiene sincronizada en memoria
     * la página actual del capítulo.
     */
    private updateChapterPageInMemory(
        page: number,
    ): void {

        if (
            !this.currentBook
        ) {

            return;
        }


        if (
            !this.currentBook.chaptersUser
        ) {

            this.currentBook
                .chaptersUser = [];
        }


        let chapterConfig =
            this.currentBook
                .chaptersUser
                .find(
                    (config) =>
                        config.id ==
                        this.currentChapter.id,
                );


        /*
         * Si todavía no existía configuración
         * del capítulo, la creamos también al
         * guardar únicamente un cambio de página.
         */
        if (!chapterConfig) {

            chapterConfig = {

                id:
                    this.currentChapter.id,

                currentPage:
                    this.currentChapter
                        .userConfig
                        .currentPage ||
                    1,

                currentPageGroup:
                    this.currentChapter
                        .userConfig
                        .currentPageGroup ||
                    1,

                customData:
                    this.currentChapter
                        .userConfig
                        .customData ||
                    {},

                customDataGroup:
                    this.currentChapter
                        .userConfig
                        .customDataGroup ||
                    {},
            };


            this.currentBook
                .chaptersUser
                .push(
                    chapterConfig,
                );
        }


        chapterConfig.currentPage =
            page;
        chapterConfig.hasPersonalProgress = true;
    }


    /*
     * =========================================================
     * GUARDADO EN BACKEND
     * =========================================================
     */


    /*
     * Guarda en el backend el estado actual del visor.
     *
     * Actualmente una única petición guarda:
     *
     * - página actual;
     * - anotaciones;
     * - libro;
     * - capítulo;
     * - modo individual/grupo.
     */
    saveViewerConfig(): void {
        // Una capa de consulta nunca se guarda: el visor solo tiene un documento vacío que no debe sustituir a los datos.
        if (!this.currentBook || !this.currentChapter || !this.currentChapter.userConfig || this.annotationReadOnly) { return; }
        this.annotationsLoadError = readAnnotationData(this.currentAnnotationsJson).error;
        if (this.annotationsLoadError || this.documentLoadError || (this.pdfViewer && this.pdfViewer.pdfLoadError)) { return; }
        if (this.isGroupMode && this.currentBook.currentGroup && this.annotationLayer === 'shared') {
            if (this.annotationsDirty && this.sharedCanEdit && !this.sharedLoadError && this.sharedData &&
                this.viewerPersistence.enqueueShared(Number(this.currentBook.currentGroup), Number(this.currentBook.id),
                    Number(this.currentChapter.id), JSON.stringify(this.sharedData))) { this.annotationsDirty = false; }
            return;
        }
        const payload = this.getDataToSaveConfig();
        if (this.isGroupMode && this.currentBook.currentGroup && this.annotationLayer === 'personal') {
            payload.isGroupMode = 0;
            payload.page_current = this.currentPage;
            payload.custom_data = this.currentChapter.userConfig.customData;
        }
        // Capturar identidad y JSON inmutables antes de cualquier espera o cambio de tema/modo/libro.
        payload.custom_data = typeof payload.custom_data === "string" ? payload.custom_data : JSON.stringify(payload.custom_data);
        if (this.viewerPersistence.enqueue(payload)) { this.annotationsDirty = false; }
    }

    private prepareAnnotationsSave(): void {
        if (this.pdfViewer) { this.pdfViewer.finishAnnotationEdit(); }
        this.flushPendingAnnotationsSave();
    }

    private withSavedAnnotations(action: () => void, rejected: () => void = () => undefined): void {
        if (this.transitionPending) { return; }
        this.prepareAnnotationsSave();
        if (!this.viewerPersistence.hasPending) { action(); return; }
        this.transitionPending = true;
        this.transitionSubscription = this.viewerPersistence.beforeLeave().subscribe(saved => {
            this.transitionPending = false;
            if (saved) { action(); } else { rejected(); }
        });
    }


    /*
     * Este método se mantiene como alias temporal
     * por si todavía existe alguna llamada desde el HTML
     * o desde otro punto de la aplicación.
     *
     * Cuando comprobemos que ya no se utiliza podemos
     * eliminarlo definitivamente.
     */
    saveConfig(): void {

        this.saveViewerConfig();
    }


    /*
     * Construye el objeto que espera actualmente
     * el backend de EdicionesEO.
     *
     * Conservamos el mismo contrato que utilizaba
     * anteriormente el visor.
     */
    getDataToSaveConfig() {

        return {

            isGroupMode:
                this.isGroupMode
                    ? 1
                    : 0,

            // Laravel exige página junto a las anotaciones: las históricas conservan su página de grupo.
            page_current:
                this.isGroupMode
                    ? this.storedPage(this.currentChapter.userConfig.currentPageGroup)
                    : this.currentPage,

            custom_data:
                this.isGroupMode
                    ? this.currentChapter
                          .userConfig
                          .customDataGroup
                    : this.currentChapter
                          .userConfig
                          .customData,

            book_index_id:
                this.currentChapter.id,

            book_id:
                this.currentBook.id,

            chapter_id:
                this.currentChapter.id,
        };
    }

    // Laravel puede devolver la página como texto; la cola exige un entero positivo.
    private storedPage(value: any): number {
        const page = Number(value);
        return Number.isInteger(page) && page > 0 ? page : 1;
    }

    // Capas dibujadas sin interacción: las visibles que no son la activa editable (la activa de consulta también).
    get passiveAnnotations(): any[] {
        if (!this.groupLayers) { return []; }
        const readOnly = this.annotationReadOnly;
        return ['personal', 'historical', 'shared'].reduce((all, layer) => this.layerVisibility[layer] &&
            (layer !== this.annotationLayer || readOnly) ? all.concat(this.layerAnnotations(layer)) : all, []);
    }

    // Capa activa de solo consulta: Históricas siempre; Compartidas sin permiso de edición (alumno) o sin datos válidos.
    // Se ven y se listan en el panel, pero no se editan ni se guardan.
    get annotationReadOnly(): boolean {
        return !!(this.isGroupMode && this.currentBook && this.currentBook.currentGroup && (this.annotationLayer === 'historical' ||
            (this.annotationLayer === 'shared' && (!this.sharedCanEdit || !this.sharedData || this.sharedLoadError))));
    }

    private get groupLayers(): boolean {
        return !!(this.isGroupMode && this.currentBook && this.currentBook.currentGroup && this.currentChapter &&
            this.currentChapter.userConfig);
    }

    // Datos originales de una capa, sin copiarlos ni normalizarlos.
    private layerRaw(layer: string): any {
        if (layer === 'shared') { return this.sharedData; }
        return layer === 'personal' ? this.currentChapter.userConfig.customData : this.currentChapter.userConfig.customDataGroup;
    }

    private layerAnnotations(layer: string): PdfAnnotation[] {
        const data = this.layerRaw(layer);
        if (!data) { return []; }
        const result = readAnnotationData(data);
        return result.error ? [] : result.annotations;
    }

    // Error de lectura de la capa activa; en solo lectura se comprueban sus datos reales, no el documento vacío del visor.
    private activeLayerError(): string | null {
        return readAnnotationData(this.annotationReadOnly ? this.layerRaw(this.annotationLayer) : this.currentAnnotationsJson).error;
    }

    // Anotaciones de la capa activa de solo consulta: se dibujan como pasivas (si es visible) y se listan en el panel.
    get readOnlyAnnotations(): PdfAnnotation[] {
        return this.groupLayers && this.annotationReadOnly ? this.layerAnnotations(this.annotationLayer) : [];
    }

    // La capa activa editable está oculta en el PDF: el visor no la dibuja y no permite editarla hasta mostrarla.
    get annotationsHidden(): boolean {
        return this.groupLayers && !this.annotationReadOnly && !this.layerVisibility[this.annotationLayer];
    }

    get layerVisibilityOptions(): PdfLayerVisibilityOption[] {
        const shared = this.sharedLoading ? 'loading' : this.sharedLoadError ? 'error' : this.sharedData ? 'ok' : 'none';
        const key = this.groupLayers ? `${shared}:${this.layerVisibility.personal}:${this.layerVisibility.shared}:` +
            `${this.layerVisibility.historical}` : '';
        if (key === this.visibilityOptionsKey) { return this.visibilityOptions; }
        this.visibilityOptionsKey = key;
        this.visibilityOptions = !this.groupLayers ? [] : [
            { value: 'personal', label: 'Personales', visible: this.layerVisibility.personal },
            { value: 'shared', label: 'Compartidas', visible: this.layerVisibility.shared, disabled: shared !== 'ok',
                hint: shared === 'loading' ? 'Cargando…' : shared === 'ok' ? undefined : 'No disponible' },
            { value: 'historical', label: 'Históricas', visible: this.layerVisibility.historical }];
        return this.visibilityOptions;
    }

    // Solo cambia lo que se dibuja: no toca anotaciones, no marca cambios y no envía nada.
    setLayerVisibility(change: { value: string; visible: boolean }): void {
        if (!change || !(change.value in this.layerVisibility)) { return; }
        this.layerVisibility = { ...this.layerVisibility, [change.value]: !!change.visible };
    }

    /*
     * Opciones del selector de capa de la barra del visor. Solo describe el estado actual:
     * changeAnnotationLayer y onAnnotationsChange siguen aplicando los permisos.
     * Se reutiliza el mismo array mientras no cambie el estado, para no redibujar el menú.
     */
    get annotationLayerOptions(): PdfAnnotationLayerOption[] {
        const available = this.isGroupMode && this.currentBook && this.currentBook.currentGroup && this.currentChapter;
        const shared = this.sharedLoading ? 'loading' : this.sharedLoadError ? 'error' :
            !this.sharedData ? 'none' : this.sharedCanEdit ? 'edit' : 'read';
        const key = available ? `${shared}:${this.annotationLayer}` : '';
        if (key === this.layerOptionsKey) { return this.layerOptions; }
        this.layerOptionsKey = key;
        this.layerOptions = [];
        if (!available) { return this.layerOptions; }
        const active = this.annotationLayer === 'shared';
        this.layerOptions.push({ value: 'personal', label: 'Personales' });
        if (shared === 'edit') {
            this.layerOptions.push({ value: 'shared', label: 'Compartidas' });
        } else if (shared === 'read') {
            this.layerOptions.push({ value: 'shared', label: 'Compartidas', hint: 'Solo lectura', readOnly: true });
        } else if (shared === 'loading') {
            this.layerOptions.push({ value: 'shared', label: 'Compartidas', hint: 'Cargando…', readOnly: active, disabled: true });
        } else if (shared === 'error' || active) {
            this.layerOptions.push({ value: 'shared', label: 'Compartidas', hint: 'No disponible', readOnly: true, disabled: true });
        }
        this.layerOptions.push({ value: 'historical', label: 'Históricas', hint: 'Solo consulta', readOnly: true });
        return this.layerOptions;
    }

    get sharedPageNotes(): any[] {
        // Con Compartidas activa (también en solo lectura) sus notas se consultan en el panel del visor.
        if (!this.sharedData || this.annotationLayer === 'shared' || !this.layerVisibility.shared) { return []; }
        return this.sharedData.annotations.filter(item => item.page === this.currentPage && item.type === 'note');
    }

    changeAnnotationLayer(layer: 'personal' | 'historical' | 'shared'): void {
        // Sin permiso de edición también se puede abrir para verla: el guardado compartido sigue exigiendo sharedCanEdit.
        if (layer === this.annotationLayer || (layer === 'shared' && !this.sharedData)) { return; }
        this.withSavedAnnotations(() => {
            this.annotationLayer = layer;
            this.annotationContext = `${this.currentBook.id}:${this.currentChapter.id}:${layer}:${++this.contextRevision}`;
            this.annotationsLoadError = this.activeLayerError();
            this.shareFeedback = null;
        });
    }

    /*
     * Compartir con este grupo: copia una anotación de Personales o Históricas en las Compartidas del grupo actual.
     * Reutiliza enqueueShared, que envía el documento con la última revisión confirmada: si otra sesión ha escrito
     * antes, Laravel devuelve 409 sin escribir y la copia se conserva aquí. La original no se modifica.
     * El ID es determinista por cuenta, capa y original (original~p<usuario> o ~h<usuario>): una segunda pulsación,
     * un reintento o una recarga no crean duplicados, y dos cuentas con el mismo ID de origen (los IDs del visor son
     * prefijo + Date.now(), sin usuario) obtienen copias distintas. El sufijo final ~[ph]<entero> no es ambiguo.
     */
    get canShareAnnotations(): boolean {
        return this.groupLayers && (this.annotationLayer === 'personal' || this.annotationLayer === 'historical') &&
            this.sharedCanEdit && !!this.sharedData && !this.sharedLoading && !this.sharedLoadError &&
            !this.viewerPersistence.needsLocalCopy && this.shareUserId !== null;
    }

    private get shareUserId(): number | null {
        const id = this.currentUser ? Number(this.currentUser.id) : NaN;
        return Number.isInteger(id) && id > 0 ? id : null;
    }

    private sharedCopyId(id: string, layer: string = this.annotationLayer): string | null {
        const user = this.shareUserId;
        return user === null ? null : `${id}~${layer === 'historical' ? 'h' : 'p'}${user}`;
    }

    readonly isAnnotationShared = (annotation: PdfAnnotation): boolean => {
        const id = annotation ? this.sharedCopyId(annotation.id) : null;
        return !!(id && this.sharedData && this.sharedData.annotations.some(item => item.id === id));
    }

    get shareStatus(): string | null {
        const feedback = this.shareFeedback;
        if (!feedback) { return null; }
        if (feedback.message) { return feedback.message; }
        if (this.viewerPersistence.hasConflict) {
            return 'No se ha compartido: las compartidas han cambiado en otra sesión. La copia se conserva en el visor ' +
                'hasta que recargues; la original no cambia.';
        }
        if (this.viewerPersistence.error) { return 'La copia no se ha confirmado todavía; se conserva en el visor.'; }
        return this.viewerPersistence.hasPending ? 'Compartiendo con este grupo…' : 'Compartida con este grupo.';
    }

    shareAnnotation(annotation: PdfAnnotation): void {
        if (!annotation || !this.canShareAnnotations) { return; }
        const layer = this.annotationLayer;
        // Un texto en edición se confirma antes, para copiar lo que se ve.
        if (layer === 'personal' && this.pdfViewer) { this.pdfViewer.finishAnnotationEdit(); }
        const source = readAnnotationData(this.layerRaw(layer));
        const original = source.error ? null : source.annotations.find(item => item.id === annotation.id);
        if (!original) {
            this.shareFeedback = { id: annotation.id, message: 'No se ha compartido: la anotación ya no existe.' };
            return;
        }
        const id = this.sharedCopyId(original.id, layer);
        if (!id) { return; }
        if (this.sharedData.annotations.some(item => item.id === id)) {
            this.shareFeedback = { id, message: 'Ya está compartida con este grupo.' };
            return;
        }
        const copy = { ...JSON.parse(JSON.stringify(original)), id };
        const next = { ...this.sharedData, annotations: this.sharedData.annotations.concat([copy]) };
        if (readAnnotationData(next).error || !this.viewerPersistence.enqueueShared(Number(this.currentBook.currentGroup),
            Number(this.currentBook.id), Number(this.currentChapter.id), JSON.stringify(next))) {
            this.shareFeedback = { id, message: this.viewerPersistence.error || 'No se ha podido compartir la anotación.' };
            return;
        }
        this.sharedData = next;
        this.shareFeedback = { id };
    }

    leaveExpiredSession(): void {
        if (!this.viewerPersistence.expiredSessionWithConflict) { return; }
        this.viewerPersistence.approveForcedExit();
        this.store.dispatch(new AuthActions.DoLogout(true));
    }

    loadSharedAnnotations(): void {
        if (!this.currentBook || !this.currentChapter || !this.isGroupMode || !this.currentBook.currentGroup ||
            this.viewerPersistence.hasPending || this.annotationsDirty) { return; }
        const group = Number(this.currentBook.currentGroup);
        const book = Number(this.currentBook.id);
        const chapter = Number(this.currentChapter.id);
        const generation = ++this.sharedLoadRevision;
        if (this.sharedLoadSubscription) { this.sharedLoadSubscription.unsubscribe(); }
        this.sharedData = null;
        this.sharedCanEdit = false;
        this.sharedLoadError = null;
        this.sharedLoading = true;
        const failed = () => {
            if (generation !== this.sharedLoadRevision) { return; }
            this.sharedLoading = false;
            this.sharedLoadError = 'No se han podido cargar las anotaciones compartidas. Reinténtalo.';
        };
        this.sharedLoadSubscription = this.booksService.getGroupAnnotations(group, book, chapter).pipe(first(), timeout(30000)).subscribe(response => {
            if (generation !== this.sharedLoadRevision) { return; }
            if (!response || response.group_id !== group || response.book_id !== book || response.chapter_id !== chapter ||
                typeof response.can_edit_shared !== 'boolean' || !Number.isInteger(response.revision) || response.revision < 0 ||
                !response.shared || response.shared.version !== 1 || !Array.isArray(response.shared.annotations) ||
                readAnnotationData(response.shared).error || (response.revision === 0 && response.shared.annotations.length)) {
                failed(); return;
            }
            this.sharedData = response.shared;
            this.sharedCanEdit = response.can_edit_shared;
            this.viewerPersistence.setSharedRevision(group, book, chapter, response.revision);
            this.sharedLoading = false;
        }, failed);
    }


    /*
     * =========================================================
     * RECURSOS DEL CAPÍTULO
     * =========================================================
     */

    initResourcesByChapter() {

        this.chapterResources = [];


        const resources =
            this.currentBook.resources;


        if (
            !resources ||
            !resources.length
        ) {

            return;
        }


        const chapterStart =
            this.currentChapter.page_start;

        const chapterEnd =
            this.currentChapter.page_end;


        resources.forEach(
            (element) => {

                /*
                 * Recurso sin páginas asociadas.
                 */
                if (
                    !element.pages ||
                    !element.pages.length
                ) {

                    return;
                }


                /*
                 * Comprobamos permisos de visualización.
                 */
                if (
                    !this.resourcesService
                        .hasPermissionToShowInViewer(
                            element,
                            this.currentUser,
                        )
                ) {

                    return;
                }


                /*
                 * Añadimos únicamente los recursos
                 * asociados a las páginas de este capítulo.
                 */
                if (
                    this.checkPagesInChapter(
                        element,
                        chapterStart,
                        chapterEnd,
                    )
                ) {

                    this.chapterResources
                        .push(
                            element,
                        );
                }
            },
        );
    }


    checkPagesInChapter(
        resource,
        chapterStart,
        chapterEnd,
    ) {

        let find = false;


        if (
            resource.pages &&
            resource.pages.length
        ) {

            resource.pages
                .forEach((page) => {

                    if (
                        page >= chapterStart &&
                        page <= chapterEnd
                    ) {

                        find = true;

                        return;
                    }
                });
        }


        return find;
    }


    /*
     * Se mantiene porque existía en el componente
     * original y puede ser utilizado desde otra parte.
     */
    setViewerPages(resource) {

        const pagesToViewer = [];

        return pagesToViewer;
    }


    /*
     * =========================================================
     * CIERRE DEL VISOR
     * =========================================================
     */

    reset() {

        this.mode = "";

        this.title = "";
    }


    /*
     * El usuario no necesita confirmar ni pulsar Guardar.
     *
     * Si existe algún cambio pendiente lo persistimos
     * automáticamente antes de cerrar.
     */
    close() {
        this.withSavedAnnotations(() => this.actionClose());
    }


    private releaseClosedPdf() {
        if (this.pdfClosed) { return; }
        this.sharedLoadRevision++;
        if (this.sharedLoadSubscription) { this.sharedLoadSubscription.unsubscribe(); this.sharedLoadSubscription = null; }
        if (this.pdfViewer) { this.pdfViewer.finishAnnotationEdit(); }
        this.flushPendingAnnotationsSave();
        this.pdfClosed = true;
        this.chapterLoadRevision++;
        this.documentPdf = null;
        this.fileData = null;
    }

    actionClose() {
        this.releaseClosedPdf();

        this.reset();

        this.closeFullscreen();


        this.store.dispatch(
            new AsideActions.ChangeHide(
                false,
            ),
        );

        this.store.dispatch(
            new AsideItemActions
                .ResetModalContent(),
        );

        this.store.dispatch(
            new LayoutActions
                .CloseModalViewer(),
        );

        this.store.dispatch(
            new LayoutActions
                .ClosePanelViewerResources(),
        );
    }


    /*
     * =========================================================
     * PANTALLA COMPLETA
     * =========================================================
     */

    toogleFullScreen() {

        if (
            this.isInFullScreenMode
        ) {

            this.closeFullscreen();

        } else {

            this.openFullscreen();
        }
    }


    openFullscreen() {

        if (
            this.elem.requestFullscreen
        ) {

            this.elem
                .requestFullscreen();

        } else if (
            this.elem.mozRequestFullScreen
        ) {

            /*
             * Firefox
             */
            this.elem
                .mozRequestFullScreen();

        } else if (
            this.elem.webkitRequestFullscreen
        ) {

            /*
             * Chrome / Safari
             */
            this.elem
                .webkitRequestFullscreen();

        } else if (
            this.elem.msRequestFullscreen
        ) {

            /*
             * IE / Edge antiguos
             */
            this.elem
                .msRequestFullscreen();
        }


        this.isInFullScreenMode =
            true;
    }


    closeFullscreen() {

        if (
            !this.isInFullScreenMode
        ) {

            return;
        }


        if (
            this.document.exitFullscreen
        ) {

            this.document
                .exitFullscreen();

        } else if (
            this.document.mozCancelFullScreen
        ) {

            this.document
                .mozCancelFullScreen();

        } else if (
            this.document.webkitExitFullscreen
        ) {

            this.document
                .webkitExitFullscreen();

        } else if (
            this.document.msExitFullscreen
        ) {

            this.document
                .msExitFullscreen();
        }


        this.isInFullScreenMode =
            false;
    }


    /*
     * =========================================================
     * PANEL LATERAL DE RECURSOS
     * =========================================================
     */

    clickAsideCollapsed(ev) {

        ev.preventDefault();


        this.store.dispatch(
            new LayoutActions
                .ToggleCollapsedResources(),
        );
    }


    isPanelActive(type) {

        return (
            this.openViewerPanelResources &&
            this.panelResourcesTypeOpen ==
                type
        );
    }


    openPanelResources(
        ev,
        type,
    ) {

        /*
         * Si pulsamos sobre el panel que ya
         * está abierto, lo cerramos.
         */
        if (
            this.openViewerPanelResources &&
            this.panelResourcesTypeOpen ==
                type
        ) {

            this.store.dispatch(
                new LayoutActions
                    .ClosePanelViewerResources(),
            );

            return;
        }


        /*
         * Indicamos qué tipo de recurso
         * queremos mostrar.
         */
        this.store.dispatch(
            new ViewerActions.SetPanelView({
                view: type,
            }),
        );


        /*
         * Abrimos el panel si estaba cerrado.
         */
        if (
            !this.openViewerPanelResources
        ) {

            noScroll.on();


            this.store.dispatch(
                new LayoutActions
                    .OpenPanelViewerResources(),
            );
        }
    }


    /*
     * =========================================================
     * MENÚ MÓVIL
     * =========================================================
     */

    toggleMobilePanel() {

        this.openOptionsMobileViewer =
            !this.openOptionsMobileViewer;
    }


    hasClassMobileMenu() {

        return (
            !this.openOptionsMobileViewer &&
            this.openModalViewer
        );
    }
}
